import { appsSearchQueryKeys } from "app/pages/sales-intelligence/modules/apps-search/query/key-factory/factory";
import type { AppsSearchRequestDto } from "app/pages/sales-intelligence/modules/apps-search/types/dto";
import {
    leadsSearchQueryKeys,
    type LeadsSearchRequestDto,
    type LeadsType,
} from "app/pages/sales-intelligence/modules/leads-search";
import { contactsQueryKeys } from "app/pages/sales-intelligence/sub-modules/contacts-search/query/queryKeys/queryKeyFactory";
import type { ContactsSearchResultsRequestDto } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";
import type { OpportunityListId } from "app/pages/sales-intelligence/sub-modules/opportunities/types";

import { BASE_QUERY_KEY } from "./const";

/**
 * @description
 * Creates React Query keys for opportunity list operations
 *
 * @dependency `leadsSearchQueryKeys`
 * This dependency serves two purposes:
 * 1. **Query Key Hierarchy**: Establishes a parent-child relationship where opportunity lists
 *    are a specialization of leads search, scoped to a specific list
 * 2. **Cache Invalidation**: When `leadsSearchQueryKeys.tableData()` is invalidated, all
 *    dependent queries (including opportunity lists) are automatically invalidated
 */
export const opportunityListQueryKeyFactory = {
    leadsTableDataBaseKey() {
        return [...leadsSearchQueryKeys.tableData(), BASE_QUERY_KEY] as const;
    },
    leadsTableData(listId: OpportunityListId, sourceType?: LeadsType, dto?: LeadsSearchRequestDto) {
        const base = [...opportunityListQueryKeyFactory.leadsTableDataBaseKey(), listId] as const;

        if (sourceType && dto) {
            return [...base, sourceType, dto];
        }

        if (sourceType) {
            return [...base, sourceType];
        }

        if (dto) {
            return [...base, dto];
        }

        return base;
    },

    appsTableDataBaseKey() {
        return [...appsSearchQueryKeys.tableData(), BASE_QUERY_KEY] as const;
    },
    appsTableData(listId: OpportunityListId | undefined, dto?: AppsSearchRequestDto) {
        const base = [...opportunityListQueryKeyFactory.appsTableDataBaseKey(), listId] as const;

        if (dto) {
            return [...base, dto];
        }

        return base;
    },
} as const;

/**
 * @description
 * Creates React Query keys for opportunity contacts operations
 *
 * @note
 * We might want to add a depenendcy on the `contact-search` query keys in the future
 *
 * @independence
 * This factory is independent from `leads-search` because:
 * 1. **Separate Cache Management**: Contacts cache should be managed independently from leads cache
 * 3. **Isolated Invalidation**: Invalidating leads searches should not affect contacts queries
 */
export const opportunityContactsQueryKeys = {
    tableData(listId?: OpportunityListId, sourceType?: LeadsType, dto?: ContactsSearchResultsRequestDto) {
        return listId
            ? contactsQueryKeys.tableData(sourceType, dto, [BASE_QUERY_KEY, listId])
            : contactsQueryKeys.tableData(sourceType, dto, [BASE_QUERY_KEY]);
    },
} as const;

export const getOpportunityListTableQueryKeys = (listId?: OpportunityListId) =>
    [
        listId
            ? opportunityListQueryKeyFactory.leadsTableData(listId)
            : opportunityListQueryKeyFactory.leadsTableDataBaseKey(),
        opportunityContactsQueryKeys.tableData(listId),
        listId
            ? opportunityListQueryKeyFactory.appsTableData(listId)
            : opportunityListQueryKeyFactory.appsTableDataBaseKey(),
    ] as const;
